import { Flex, Skeleton, Text } from '@chakra-ui/react';
import type { CoinState } from '@halo/wallet-app';
import { useWalletStore } from '@/wallet';
import { ItemList, type ItemListProps } from '../item-list';
import { CoinIcon, type CoinIconProps } from './coin-icon';

export interface CoinItemProps extends ItemListProps {
  coin: CoinState;
  priceLoading?: boolean;
  balanceLoading?: boolean;
  coinIconProps?: Omit<CoinIconProps, 'coin'>;
}

export function CoinItem({
  coin,
  priceLoading,
  balanceLoading,
  coinIconProps,
  ...props
}: CoinItemProps) {
  const { coinAmountDisplay, currencyAmountDisplay } = useWalletStore();
  const changePercent = Number(coin.priceChangePercent);
  const isNotZero = changePercent !== 0;
  const isPositive = changePercent > 0;

  return (
    <ItemList
      icon={<CoinIcon coin={coin} {...coinIconProps} />}
      title={coin.symbol}
      description={
        <Flex alignItems="center" gap="0.5" fontSize="xs" fontWeight="medium">
          {priceLoading ? (
            <Skeleton height="16px" width="100px" />
          ) : (
            <>
              <Text color="fg.subtle">{currencyAmountDisplay(coin.currencyPrice, '')}</Text>
              {isNotZero && (
                <Text color={isPositive ? 'green.100' : 'red.100'}>
                  {isPositive ? '+' : '-'}
                  {coin.priceChangePercent}%
                </Text>
              )}
            </>
          )}
        </Flex>
      }
      endElement={
        balanceLoading ? (
          <>
            <Skeleton height="16px" width="30px" />
            <Skeleton height="16px" width="20px" mt="1" />
          </>
        ) : (
          <>
            <Text fontWeight="semibold">{coinAmountDisplay(coin.balance)}</Text>
            <Text fontWeight="medium" color="fg.subtle">
              {currencyAmountDisplay(coin.currencyBalance)}
            </Text>
          </>
        )
      }
      _hover={{ bg: 'transparent' }}
      {...props}
    />
  );
}
